import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ApiError } from '@/auth/api';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import { buyDeviceRequest, getMyPurchase, type BuyerPurchase } from '@/features/account/api';
import { goToCheckout } from '@/features/account/checkout';
import { describeReservedDevice } from '@/features/account/reservations';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { StatusChip } from '@/components/StatusChip';

/// While the notification has not arrived the page asks again, every few seconds, for about
/// half a minute; after that it says the payment is still being processed.
export const POLL_INTERVAL_MS = 3000;
export const POLL_ATTEMPTS = 10;

type PageState =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | { status: 'ready'; purchase: BuyerPurchase };

type RetryState = 'idle' | 'starting' | 'unavailable' | 'failed';

const titles: Record<BuyerPurchase['status'], string> = {
  CONFIRMED: '¡Listo, compraste tu equipo!',
  PROCESSING: 'Estamos confirmando tu pago',
  REJECTED: 'Tu pago no se aprobó',
  REFUND_PENDING: 'Tu pago llegó tarde',
  REFUNDED: 'Te devolvimos el pago',
  NOT_COMPLETED: 'La compra no se concretó',
};

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

/**
 * Where Mercado Pago sends the buyer back, and the page of each purchase in "Mis compras" (VEN-7).
 *
 * It reads only `?purchase=` and asks the backend how that purchase stands. Whatever else Mercado
 * Pago adds to the URL (`status`, `collection_status`) is ignored: a return confirms nothing
 * (BR-045). Someone else's purchase answers the same "not found" as a missing one.
 */
export function CheckoutReturnPage() {
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get('purchase') ?? '';
  const { token } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [polls, setPolls] = useState(0);
  const [retry, setRetry] = useState<RetryState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const fetchPurchase = useCallback(
    async (quiet: boolean) => {
      if (!token) return;
      if (!purchaseId) {
        setState({ status: 'missing' });
        return;
      }
      if (!quiet) setState({ status: 'loading' });
      try {
        setState({ status: 'ready', purchase: await getMyPurchase(token, purchaseId) });
      } catch (error) {
        // A failed re-check keeps what was already shown; only the first read shows the error.
        if (quiet) return;
        setState({
          status: error instanceof ApiError && error.status === 404 ? 'missing' : 'error',
        });
      }
    },
    [token, purchaseId],
  );

  useEffect(() => {
    setPolls(0);
    void fetchPurchase(false);
  }, [fetchPurchase]);

  const processing = state.status === 'ready' && state.purchase.status === 'PROCESSING';
  const stillWaiting = processing && polls < POLL_ATTEMPTS;

  useEffect(() => {
    if (!stillWaiting) return;
    timer.current = setTimeout(() => {
      setPolls((value) => value + 1);
      void fetchPurchase(true);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer.current);
  }, [stillWaiting, polls, fetchPurchase]);

  const checkAgain = () => {
    setPolls(0);
    void fetchPurchase(true);
  };

  const buyAgain = async (deviceId: string) => {
    if (!token) return;
    setRetry('starting');
    try {
      goToCheckout((await buyDeviceRequest(token, deviceId)).checkoutUrl);
    } catch (error) {
      setRetry(error instanceof ApiError && error.status === 409 ? 'unavailable' : 'failed');
    }
  };

  const purchase = state.status === 'ready' ? state.purchase : null;

  return (
    <NarrowPage maxWidth={640}>
      <PageHeader
        eyebrow="TU COMPRA"
        title={purchase ? titles[purchase.status] : 'Tu compra'}
        description="Con la referencia, PhoneTec identifica tu compra cuando te contacta."
      />
      {state.status === 'loading' ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={24} color="secondary" />
          <Typography>Consultando tu compra...</Typography>
        </Stack>
      ) : null}
      {state.status === 'missing' ? (
        <Alert severity="error">
          No encontramos esa compra en tu cuenta.{' '}
          <Button component={RouterLink} to="/account" size="small" color="inherit">
            Ver mis compras
          </Button>
        </Alert>
      ) : null}
      {state.status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void fetchPurchase(false)}>
              Reintentar
            </Button>
          }
        >
          No pudimos consultar tu compra.
        </Alert>
      ) : null}
      {purchase ? (
        <SectionCard title={describeReservedDevice(purchase.device)}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" component="p">
                Compra #{purchase.reference}
              </Typography>
              <StatusChip domain="purchase" status={purchase.status} />
            </Stack>
            <Detail label="Total" value={formatNullablePrice(purchase.priceMinor)} />

            {purchase.status === 'CONFIRMED' ? (
              <Alert severity="success">
                <strong>Próximos pasos.</strong> El equipo es tuyo y se retira en el local: PhoneTec
                te contacta por WhatsApp para coordinar el retiro. Te mandamos un mail con los datos
                de la compra.
              </Alert>
            ) : null}

            {processing && stillWaiting ? (
              <Alert severity="info" icon={<CircularProgress size={20} color="inherit" />}>
                Estamos esperando la confirmación de Mercado Pago. Esto puede tardar unos segundos;
                no hace falta que pagues de nuevo.
              </Alert>
            ) : null}
            {processing && !stillWaiting ? (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={checkAgain}>
                    Volver a consultar
                  </Button>
                }
              >
                {purchase.canResumePayment
                  ? `Todavía no recibimos tu pago. Te guardamos el equipo hasta el ${formatHistoryTimestamp(purchase.expiresAt)}: si no llegaste a pagar, podés continuar en Mercado Pago.`
                  : `Tu pago sigue en proceso. Te guardamos el equipo hasta el ${formatHistoryTimestamp(purchase.expiresAt)}; cuando Mercado Pago lo confirme te avisamos por mail.`}
              </Alert>
            ) : null}

            {purchase.status === 'REJECTED' || purchase.status === 'NOT_COMPLETED' ? (
              <Alert severity={purchase.status === 'REJECTED' ? 'error' : 'warning'}>
                {purchase.status === 'NOT_COMPLETED'
                  ? 'No recibimos el pago a tiempo y el equipo se liberó. No se te cobró nada.'
                  : purchase.held
                    ? `Mercado Pago rechazó el pago y no se te cobró nada. Te guardamos el equipo hasta el ${formatHistoryTimestamp(purchase.expiresAt)}: podés intentar con otro medio de pago.`
                    : 'Mercado Pago rechazó el pago y no se te cobró nada. El equipo se liberó; si sigue disponible, podés volver a comprarlo.'}
              </Alert>
            ) : null}
            {retry === 'unavailable' ? (
              <Alert severity="error">
                Este equipo ya no está disponible.{' '}
                <Button component={RouterLink} to="/catalog" size="small" color="inherit">
                  Ver otros equipos
                </Button>
              </Alert>
            ) : null}
            {retry === 'failed' ? (
              <Alert severity="error">
                No pudimos conectar con Mercado Pago. Volvé a intentar.
              </Alert>
            ) : null}

            {purchase.status === 'REFUND_PENDING' ? (
              <Alert severity="warning">
                Mercado Pago aprobó el pago cuando ya habían pasado los 30 minutos y el equipo se
                había liberado. PhoneTec te devuelve el pago y te contacta.
              </Alert>
            ) : null}
            {purchase.status === 'REFUNDED' ? (
              <Alert severity="info">PhoneTec ya te devolvió el pago de esta compra.</Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {(purchase.status === 'REJECTED' || purchase.status === 'NOT_COMPLETED') &&
              retry !== 'unavailable' ? (
                <PrimaryButton
                  disabled={retry === 'starting'}
                  onClick={() => void buyAgain(purchase.deviceId)}
                >
                  {retry === 'starting' ? 'Yendo a Mercado Pago...' : 'Volver a intentar'}
                </PrimaryButton>
              ) : null}
              {/* Only once the checks are over: a payment just made may still be on its way. */}
              {processing && !stillWaiting && purchase.canResumePayment ? (
                <PrimaryButton
                  disabled={retry === 'starting'}
                  onClick={() => void buyAgain(purchase.deviceId)}
                >
                  {retry === 'starting' ? 'Yendo a Mercado Pago...' : 'Continuar el pago'}
                </PrimaryButton>
              ) : null}
              <Button component={RouterLink} to="/account" variant="outlined" color="inherit">
                Ver mis compras
              </Button>
              <Button component={RouterLink} to="/catalog" color="inherit">
                Seguir viendo el catálogo
              </Button>
            </Stack>
          </Stack>
        </SectionCard>
      ) : null}
    </NarrowPage>
  );
}
